import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DatePicker, TreeSelect } from "antd";
import dayjs from "dayjs";
import CommonSelect from "../../../../core/common/commonSelect";
import api from "../../../../services/api";
import { notify } from "../../../../services/toast.config";
import { uploadFileToCloudinary } from "../../../../utils/uploadFileToCloudinary";
import Loader from "../../../../core/common/loader";

// Category interface for hierarchical category structure
interface CategoryNode {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: CategoryNode[];
}

interface ProductImageData {
  id: string;
  url: string;
  name: string;
  size: number;
  publicid?: string;
  resourcetype?: string;
}

interface ProductSpecificationItem {
  key: string;
  value: string;
}

// Initial form state
const INITIAL_PRODUCT_FORM = {
  name: "",
  status: "",
  companyid: "",
  companytype: "",
  companyname: "",
  assetcategoryid: "",
  purchasedate: null,
  purchasefrom: "",
  brand: "",
  serialno: "",
  model: "",
  warranty: null,
};

// Default specification structure
const DEFAULT_SPECIFICATION: ProductSpecificationItem = { key: "", value: "" };

// Required field definitions for validation
const REQUIRED_PRODUCT_FIELDS = [
  { key: "name", label: "Product Name" },
  { key: "status", label: "Status" },
  { key: "assetcategoryid", label: "Product Category" },
  { key: "brand", label: "Brand" },
  { key: "serialno", label: "Serial Number" },
  { key: "companyid", label: "Company" },
];

interface AddProductModalProps {
  selectedCompany: any;
  categoryHierarchy: CategoryNode[];
  statusOptionsList: any[];
  refreshProductList: () => Promise<void>;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  selectedCompany,
  categoryHierarchy,
  statusOptionsList,
  refreshProductList,
}) => {
  const userSignature = localStorage.getItem("signature");
  const maxImageFileSize = parseInt(
    process.env.REACT_APP_AMS_PRODUCT_IMAGE_MAX_SIZE_MB || "5"
  );

  // Loading and processing states
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  // Form validation states
  const [invalidFormFields, setInvalidFormFields] = useState<Set<string>>(
    new Set()
  );
  const [formValidationErrors, setFormValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Form data and related states
  const [productFormData, setProductFormData] = useState(INITIAL_PRODUCT_FORM);
  const [selectedProductImage, setSelectedProductImage] =
    useState<ProductImageData | null>(null);
  const [productSpecifications, setProductSpecifications] = useState<
    ProductSpecificationItem[]
  >([{ ...DEFAULT_SPECIFICATION }]);

  // Category selection states
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [categoryBreadcrumbPath, setCategoryBreadcrumbPath] =
    useState<string>("");

  // Convert hierarchical categories to TreeSelect format
  const categoryTreeData = useMemo(() => {
    const convertCategoriesToTreeFormat = (
      categories: CategoryNode[]
    ): any[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .filter((category) => category && category.status === "Active")
        .map((category) => {
          const hasChildCategories =
            category.subcategories && category.subcategories.length > 0;

          return {
            title: category.name,
            value: category.id,
            key: category.id,
            children: hasChildCategories
              ? convertCategoriesToTreeFormat(category.subcategories!)
              : undefined,
          };
        });
    };

    return convertCategoriesToTreeFormat(categoryHierarchy || []);
  }, [categoryHierarchy]);

  // Generate category breadcrumb path for display
  const generateCategoryBreadcrumb = useCallback(
    (
      categoryId: string,
      categories: CategoryNode[] = categoryHierarchy
    ): string => {
      const findCategoryPathRecursive = (
        cats: CategoryNode[],
        targetId: string,
        currentPath: string[] = []
      ): string[] | null => {
        for (const cat of cats) {
          const newPath = [...currentPath, cat.name];

          if (cat.id === targetId) {
            return newPath;
          }

          if (cat.subcategories && cat.subcategories.length > 0) {
            const foundPath = findCategoryPathRecursive(
              cat.subcategories,
              targetId,
              newPath
            );
            if (foundPath) {
              return foundPath;
            }
          }
        }
        return null;
      };

      const pathArray = findCategoryPathRecursive(categories, categoryId);
      return pathArray ? pathArray.join(" → ") : "";
    },
    [categoryHierarchy]
  );

  // Check if any processing operation is in progress
  const isAnyProcessing = useMemo(
    () => isUploadingImage || isDeletingImage || isSubmittingForm,
    [isUploadingImage, isDeletingImage, isSubmittingForm]
  );

  // Reset form to initial state
  const resetProductForm = useCallback(() => {
    const { company_id, company_name, company_type } = selectedCompany;
    setProductFormData({
      ...INITIAL_PRODUCT_FORM,
      companyid: company_id,
      companyname: company_name,
      companytype: company_type,
    });
    setSelectedProductImage(null);
    setProductSpecifications([{ ...DEFAULT_SPECIFICATION }]);
    setInvalidFormFields(new Set());
    setFormValidationErrors({});
    setIsDeletingImage(false);
    setSelectedCategoryId("");
    setCategoryBreadcrumbPath("");
  }, [selectedCompany]);

  // Clear specific field error
  const clearSpecificFieldError = useCallback((fieldName: string) => {
    setInvalidFormFields((prev) => {
      const updatedSet = new Set(prev);
      updatedSet.delete(fieldName);
      return updatedSet;
    });
    setFormValidationErrors((prev) => {
      const updatedErrors = { ...prev };
      delete updatedErrors[fieldName];
      return updatedErrors;
    });
  }, []);

  // Validate uploaded file
  const validateUploadedFile = useCallback(
    (file: File): string | null => {
      if (!file.type.startsWith("image/")) {
        return "Only image files are allowed";
      }
      if (file.size > maxImageFileSize * 1024 * 1024) {
        return `Image must be less than ${maxImageFileSize}MB`;
      }
      return null;
    },
    [maxImageFileSize]
  );

  // Comprehensive form validation
  const validateProductForm = useCallback((): boolean => {
    const validationErrors: { [key: string]: string } = {};
    const invalidFields = new Set<string>();

    // Validate required fields
    REQUIRED_PRODUCT_FIELDS.forEach(({ key, label }) => {
      const fieldValue = productFormData[key as keyof typeof productFormData];
      if (!fieldValue) {
        validationErrors[key] = `${label} is required`;
        invalidFields.add(key);
      }
    });

    // Custom validation for category selection
    if (!selectedCategoryId) {
      validationErrors["assetcategoryid"] = "Category is required";
      invalidFields.add("assetcategoryid");
    }

    // Validate product specifications
    productSpecifications.forEach((spec, index) => {
      const specValidationErrors: string[] = [];
      const hasSpecKey = spec.key.trim();
      const hasSpecValue = spec.value.trim();

      if (hasSpecKey && !hasSpecValue) {
        specValidationErrors.push("Value is required when key is provided");
      }
      if (hasSpecValue && !hasSpecKey) {
        specValidationErrors.push("Key is required when value is provided");
      }
      if (hasSpecKey) {
        const duplicateIndex = productSpecifications.findIndex(
          (s, i) =>
            i !== index &&
            s.key.trim().toLowerCase() === hasSpecKey.toLowerCase()
        );
        if (duplicateIndex !== -1) {
          specValidationErrors.push("Duplicate specification key");
        }
      }

      if (specValidationErrors.length > 0) {
        validationErrors[`spec-${index}`] = specValidationErrors[0];
        invalidFields.add(`spec-${index}`);
      }
    });

    setFormValidationErrors(validationErrors);
    setInvalidFormFields(invalidFields);
    return Object.keys(validationErrors).length === 0;
  }, [productFormData, productSpecifications, selectedCategoryId]);

  // Delete image from cloud storage
  const deleteImageFromCloudStorage = useCallback(
    async (imageToDelete: ProductImageData) => {
      if (!imageToDelete?.publicid) return;
      try {
        await api.post("userService/cloudinary/deleteFile", {
          signature: userSignature,
          publicid: imageToDelete.publicid,
          resourcetype: imageToDelete.resourcetype || "image",
        });
      } catch (error) {
        notify.info("Failed to delete image from cloud:");
      }
    },
    [userSignature]
  );

  // Handle regular input field changes
  const handleFormInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      clearSpecificFieldError(name);
      setProductFormData((prev) => ({ ...prev, [name]: value }));
    },
    [clearSpecificFieldError]
  );

  // Handle date field changes
  const handleDateFieldChange = useCallback(
    (fieldName: string, selectedDate: any) => {
      const formattedDate = selectedDate
        ? dayjs(selectedDate).format("YYYY-MM-DD")
        : null;
      clearSpecificFieldError(fieldName);
      setProductFormData((prev) => ({ ...prev, [fieldName]: formattedDate }));
    },
    [clearSpecificFieldError]
  );

  // Handle status selection change
  const handleStatusSelectionChange = useCallback(
    (selectedStatus: any) => {
      if (selectedStatus) {
        clearSpecificFieldError("status");
        setProductFormData((prev) => ({
          ...prev,
          status: selectedStatus.value,
        }));
      }
    },
    [clearSpecificFieldError]
  );

  // Handle category selection from TreeSelect
  const handleCategorySelectionChange = useCallback(
    (categoryValue: string, label: any, extra: any) => {
      setSelectedCategoryId(categoryValue);

      // Generate and set the full breadcrumb path
      const breadcrumbPath = generateCategoryBreadcrumb(categoryValue);
      setCategoryBreadcrumbPath(breadcrumbPath);

      clearSpecificFieldError("assetcategoryid");
      setProductFormData((prev) => ({
        ...prev,
        assetcategoryid: categoryValue,
      }));
    },
    [clearSpecificFieldError, generateCategoryBreadcrumb]
  );

  // Handle image file selection and upload
  const handleProductImageSelection = useCallback(
    async (selectedFiles: FileList | File[]) => {
      const selectedFile = selectedFiles[0];
      if (!selectedFile) return;

      const fileValidationError = validateUploadedFile(selectedFile);
      if (fileValidationError) {
        notify.error(`${selectedFile.name}: ${fileValidationError}`);
        return;
      }

      const previousImage = selectedProductImage;
      setIsUploadingImage(true);

      try {
        // Delete previous image if exists
        if (previousImage) {
          await deleteImageFromCloudStorage(previousImage);
        }

        const cloudinaryResponse = await uploadFileToCloudinary({
          file: selectedFile,
          folder: `cms/assets/products-images/company-${productFormData.companyid}-${productFormData.companytype}`,
        });

        setSelectedProductImage({
          id: `${cloudinaryResponse.public_id}_${Date.now()}`,
          url: cloudinaryResponse.secure_url || cloudinaryResponse.url,
          name: selectedFile.name,
          size: selectedFile.size,
          publicid: cloudinaryResponse.public_id,
          resourcetype: cloudinaryResponse.resource_type || "image",
        });

        notify.success("Image uploaded successfully");
      } catch (error) {
        notify.error(`Failed to upload ${selectedFile.name}`);
      } finally {
        setIsUploadingImage(false);
      }
    },
    [
      selectedProductImage,
      validateUploadedFile,
      deleteImageFromCloudStorage,
      productFormData.companyid,
      productFormData.companytype,
    ]
  );

  // Remove selected product image
  const removeSelectedProductImage = useCallback(async () => {
    const imageToRemove = selectedProductImage;
    setSelectedProductImage(null);
    setIsDeletingImage(true);

    if (imageToRemove) {
      await deleteImageFromCloudStorage(imageToRemove);
      notify.info("Image removed successfully");
    }
    setIsDeletingImage(false);
  }, [selectedProductImage, deleteImageFromCloudStorage]);

  // Add new specification row
  const addNewSpecification = useCallback(() => {
    setProductSpecifications((prev) => [...prev, { ...DEFAULT_SPECIFICATION }]);
  }, []);

  // Remove specification at specific index
  const removeSpecificationAtIndex = useCallback(
    (specIndex: number) => {
      if (productSpecifications.length <= 1) return;
      clearSpecificFieldError(`spec-${specIndex}`);
      setProductSpecifications((prev) =>
        prev.filter((_, i) => i !== specIndex)
      );
    },
    [productSpecifications.length, clearSpecificFieldError]
  );

  // Update specification field value
  const updateSpecificationField = useCallback(
    (
      specIndex: number,
      fieldType: keyof ProductSpecificationItem,
      newValue: string
    ) => {
      clearSpecificFieldError(`spec-${specIndex}`);
      setProductSpecifications((prev) =>
        prev.map((spec, i) =>
          i === specIndex ? { ...spec, [fieldType]: newValue } : spec
        )
      );
    },
    [clearSpecificFieldError]
  );

  // Handle form submission
  const handleProductFormSubmission = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateProductForm()) {
      notify.error("Please fix all validation errors");
      return;
    }

    try {
      setIsSubmittingForm(true);
      setIsFormLoading(true);

      const validSpecifications = productSpecifications.filter(
        (spec) => spec.key.trim() && spec.value.trim()
      );
      const specificationsObject = validSpecifications.reduce((acc, spec) => {
        acc[spec.key.trim()] = spec.value.trim();
        return acc;
      }, {} as { [key: string]: string });

      const submissionData = {
        signature: userSignature,
        name: productFormData.name,
        status: productFormData.status?.toLowerCase() === "active" ? 1 : 0,
        asset_category_id: Number(selectedCategoryId),
        purchase_date: productFormData.purchasedate || null,
        purchase_from: productFormData.purchasefrom || null,
        brand: productFormData.brand,
        serial_no: productFormData.serialno,
        model: productFormData.model || null,
        warranty: productFormData.warranty || null,
        company_id: productFormData.companyid,
        company_type: productFormData.companytype,
        image: selectedProductImage ? selectedProductImage : null,
        product_specifications: specificationsObject,
      };

      const response = await api.post("userService/addProduct", submissionData);

      resetProductForm();
      notify.success(
        response.data?.message[0]?.msg || "Product added successfully."
      );
      // Close modal
      const dismissButton = document.querySelector(
        "#add-product-dismiss-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      await refreshProductList();
    } catch (error: any) {
      const errorMessage =
        error?.data?.message?.[0]?.msg ||
        error?.data?.message ||
        "An error occurred while adding the product.";
      notify.error(errorMessage);
    } finally {
      setIsSubmittingForm(false);
      setIsFormLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    resetProductForm();
  }, [resetProductForm]);

  useEffect(() => {
    if (selectedCompany.company_id) {
      setProductFormData((prev) => ({
        ...prev,
        companyid: selectedCompany.company_id,
        companyname: selectedCompany.company_name,
        companytype: selectedCompany.company_type,
      }));
    }
  }, [selectedCompany]);

  // Render validation error message
  const renderFieldError = useCallback(
    (fieldName: string) => {
      if (!formValidationErrors[fieldName]) return null;
      return (
        <div className="text-danger mt-1">
          <small>{formValidationErrors[fieldName]}</small>
        </div>
      );
    },
    [formValidationErrors]
  );

  // Get CSS class for invalid fields
  const getFieldInvalidClass = useCallback(
    (fieldName: string) => {
      return invalidFormFields.has(fieldName) ? "is-invalid border-danger" : "";
    },
    [invalidFormFields]
  );

  return (
    <>
      {isFormLoading && <Loader />}
      <div className="modal fade" id="add_product_modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Product</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmittingForm}
                onClick={resetProductForm}>
                <i className="ti ti-x"></i>
              </button>
            </div>

            <form onSubmit={handleProductFormSubmission}>
              <div
                className="modal-body pb-0"
                style={{
                  minHeight: "400px",
                  maxHeight: "550px",
                  overflowY: "auto",
                }}>
                <div className="row">
                  {/* Product Image Upload Section */}
                  <div className="col-7 mb-4 d-flex justify-content-start">
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      {/* Avatar Container */}
                      <div className="position-relative">
                        <div
                          className="rounded-circle border border-3 border-dashed d-flex align-items-center justify-content-center position-relative overflow-hidden"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderColor: selectedProductImage
                              ? "#28a745"
                              : "#e9ecef",
                            background: selectedProductImage
                              ? "transparent"
                              : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                            cursor: !isAnyProcessing ? "pointer" : "default",
                            transition: "all 0.3s ease",
                          }}
                          onClick={() =>
                            !isAnyProcessing &&
                            document
                              .getElementById("productImageUpload")
                              ?.click()
                          }
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (!isAnyProcessing && !selectedProductImage) {
                              e.currentTarget.style.borderColor = "#0d6efd";
                              e.currentTarget.style.backgroundColor = "#f0f8ff";
                            }
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            if (!selectedProductImage) {
                              e.currentTarget.style.borderColor = "#e9ecef";
                              e.currentTarget.style.backgroundColor = "";
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (!isAnyProcessing) {
                              e.currentTarget.style.borderColor = "#e9ecef";
                              e.currentTarget.style.backgroundColor = "";
                              const droppedFiles = e.dataTransfer.files;
                              if (droppedFiles.length > 0) {
                                handleProductImageSelection(droppedFiles);
                              }
                            }
                          }}>
                          {/* Loading States */}
                          {isUploadingImage || isDeletingImage ? (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center">
                              <div
                                className={`spinner-border ${
                                  isDeletingImage
                                    ? "text-danger"
                                    : "text-primary"
                                } mb-2`}
                                role="status"
                                style={{ width: "32px", height: "32px" }}>
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                              <small className="text-muted fw-medium">
                                {isUploadingImage
                                  ? "Uploading..."
                                  : "Deleting..."}
                              </small>
                            </div>
                          ) : selectedProductImage ? (
                            /* Display Selected Image */
                            <img
                              src={selectedProductImage.url}
                              alt={selectedProductImage.name}
                              className="w-100 h-100 rounded-circle"
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            /* Empty State - No Image */
                            <div className="d-flex flex-column align-items-center justify-content-center text-center">
                              <div
                                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  background:
                                    "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)",
                                  boxShadow:
                                    "0 4px 15px rgba(13, 110, 253, 0.3)",
                                }}>
                                <i
                                  className="ti ti-camera text-white"
                                  style={{ fontSize: "20px" }}></i>
                              </div>
                              <small className="text-muted fw-medium">
                                Add Image
                              </small>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Image Information and Actions */}
                      <div
                        className="flex-grow-1"
                        style={{ maxWidth: "250px", minWidth: "200px" }}>
                        {selectedProductImage ? (
                          <div>
                            <h6
                              className="mb-1 fw-semibold"
                              style={{
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                lineHeight: "1.2",
                                fontSize: "0.9rem",
                              }}>
                              {selectedProductImage.name}
                            </h6>
                            <p className="text-muted mb-3">
                              <small>
                                <i className="ti ti-file-description me-1"></i>
                                {(
                                  selectedProductImage.size /
                                  (1024 * 1024)
                                ).toFixed(2)}{" "}
                                MB
                              </small>
                            </p>
                            <div className="d-flex gap-1 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document
                                    .getElementById("productImageUpload")
                                    ?.click();
                                }}
                                disabled={isAnyProcessing}
                                style={{
                                  borderRadius: "20px",
                                  fontSize: "0.75rem",
                                }}>
                                <i className="ti ti-refresh me-1"></i>
                                Replace
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSelectedProductImage();
                                }}
                                disabled={isAnyProcessing}
                                style={{
                                  borderRadius: "20px",
                                  fontSize: "0.75rem",
                                }}>
                                <i className="ti ti-trash me-1"></i>
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h6 className="mb-2 text-dark">
                              Upload Product Image
                            </h6>
                            <p className="text-muted mb-3">
                              <small>
                                Click the avatar or drag & drop an image
                                <br />
                                Supports JPG, PNG • Max {maxImageFileSize}MB
                              </small>
                            </p>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm px-3"
                              onClick={() =>
                                document
                                  .getElementById("productImageUpload")
                                  ?.click()
                              }
                              disabled={isUploadingImage}
                              style={{ borderRadius: "20px" }}>
                              <i className="ti ti-upload me-2"></i>
                              Choose File
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <input
                      id="productImageUpload"
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={(e) =>
                        e.target.files &&
                        handleProductImageSelection(e.target.files)
                      }
                    />
                  </div>

                  {/* Company Selection */}
                  <div className="col-md-5 mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFormFields.has("companyid")
                          ? "border border-danger rounded"
                          : ""
                      }>
                      <CommonSelect
                        className="select"
                        options={[]}
                        defaultValue={{
                          value:
                            productFormData.companyid ||
                            selectedCompany.companyid,
                          label: `${
                            productFormData.companyname ||
                            selectedCompany.companyname
                          } - ${
                            productFormData.companytype ||
                            selectedCompany.companytype
                          }`,
                        }}
                        isDisabled={true}
                        key={`company-${productFormData.companyid}-${selectedCompany.companyid}`}
                      />
                    </div>
                    {renderFieldError("companyid")}
                  </div>

                  {/* Category Selection with TreeSelect */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFormFields.has("assetcategoryid")
                          ? "border border-danger rounded p-2"
                          : ""
                      }>
                      <TreeSelect
                        showSearch
                        treeNodeFilterProp="title"
                        filterTreeNode={(input, node) => {
                          if (!input || !node) return false;
                          const inputLower = input.toLowerCase();
                          const title =
                            node.title?.toString().toLowerCase() || "";
                          return title.includes(inputLower);
                        }}
                        searchValue={undefined}
                        autoClearSearchValue={true}
                        style={{ width: "100%" }}
                        value={selectedCategoryId || undefined}
                        dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                        treeData={categoryTreeData}
                        placeholder="Please select a category"
                        allowClear
                        treeDefaultExpandAll={false}
                        onChange={handleCategorySelectionChange}
                        disabled={isSubmittingForm}
                        getPopupContainer={(triggerNode) =>
                          triggerNode.parentElement || document.body
                        }
                      />
                    </div>
                    {categoryBreadcrumbPath && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="ti ti-route me-1"></i>
                          Selected: <strong>{categoryBreadcrumbPath}</strong>
                        </small>
                      </div>
                    )}
                    {renderFieldError("assetcategoryid")}
                  </div>

                  {/* Product Name */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Product Name <span className="text-danger">*</span>
                    </label>
                    <input
                      name="name"
                      value={productFormData.name}
                      onChange={handleFormInputChange}
                      type="text"
                      className={`form-control ${getFieldInvalidClass("name")}`}
                      placeholder="Enter product name"
                      disabled={isSubmittingForm}
                    />
                    {renderFieldError("name")}
                  </div>

                  {/* Brand */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <input
                      name="brand"
                      value={productFormData.brand}
                      onChange={handleFormInputChange}
                      type="text"
                      className={`form-control ${getFieldInvalidClass(
                        "brand"
                      )}`}
                      placeholder="Enter brand name"
                      disabled={isSubmittingForm}
                    />
                    {renderFieldError("brand")}
                  </div>

                  {/* Serial Number */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Serial Number <span className="text-danger">*</span>
                    </label>
                    <input
                      name="serialno"
                      value={productFormData.serialno}
                      onChange={handleFormInputChange}
                      type="text"
                      className={`form-control ${getFieldInvalidClass(
                        "serialno"
                      )}`}
                      placeholder="Enter serial number"
                      disabled={isSubmittingForm}
                    />
                    {renderFieldError("serialno")}
                  </div>

                  {/* Model */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Model</label>
                    <input
                      name="model"
                      value={productFormData.model}
                      onChange={handleFormInputChange}
                      type="text"
                      className="form-control"
                      placeholder="Enter model (optional)"
                      disabled={isSubmittingForm}
                    />
                  </div>

                  {/* Purchase From */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Purchase From</label>
                    <input
                      name="purchasefrom"
                      value={productFormData.purchasefrom}
                      onChange={handleFormInputChange}
                      type="text"
                      className="form-control"
                      placeholder="Enter purchase source"
                      disabled={isSubmittingForm}
                    />
                  </div>

                  {/* Purchase Date */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Purchase Date</label>
                    <DatePicker
                      value={
                        productFormData.purchasedate
                          ? dayjs(productFormData.purchasedate)
                          : null
                      }
                      onChange={(date) =>
                        handleDateFieldChange("purchasedate", date)
                      }
                      className="form-control muted"
                      format="YYYY-MM-DD"
                      placeholder="Select purchase date"
                      disabled={isSubmittingForm}
                    />
                  </div>

                  {/* Warranty Date */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Warranty Date</label>
                    <DatePicker
                      value={
                        productFormData.warranty
                          ? dayjs(productFormData.warranty)
                          : null
                      }
                      onChange={(date) =>
                        handleDateFieldChange("warranty", date)
                      }
                      className="form-control muted"
                      format="YYYY-MM-DD"
                      placeholder="Select warranty date"
                      disabled={isSubmittingForm}
                    />
                  </div>

                  {/* Status Selection using CommonSelect */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFormFields.has("status")
                          ? "border border-danger rounded"
                          : ""
                      }>
                      <CommonSelect
                        className="select"
                        options={statusOptionsList}
                        defaultValue={
                          productFormData.status
                            ? {
                                value: productFormData.status,
                                label:
                                  statusOptionsList.find(
                                    (opt) =>
                                      opt.value === productFormData.status
                                  )?.label || productFormData.status,
                              }
                            : undefined
                        }
                        onChange={handleStatusSelectionChange}
                        placeHolder="Select Status"
                        isDisabled={isSubmittingForm}
                      />
                    </div>
                    {renderFieldError("status")}
                  </div>

                  {/* Product Specifications Section */}
                  <div className="col-12 mb-3">
                    <div className="border rounded-3 p-4 bg-light">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-semibold">
                          Product Specifications
                        </h6>
                      </div>

                      <div className="row g-3">
                        {productSpecifications.map(
                          (specification, specIndex) => (
                            <div key={specIndex} className="col-12">
                              <div className="row align-items-start g-2">
                                <div className="col-md-4">
                                  <input
                                    type="text"
                                    className={`form-control ${getFieldInvalidClass(
                                      `spec-${specIndex}`
                                    )}`}
                                    value={specification.key}
                                    onChange={(e) =>
                                      updateSpecificationField(
                                        specIndex,
                                        "key",
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Specification ${
                                      specIndex + 1
                                    } key`}
                                    disabled={isSubmittingForm}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <input
                                    type="text"
                                    className={`form-control ${getFieldInvalidClass(
                                      `spec-${specIndex}`
                                    )}`}
                                    value={specification.value}
                                    onChange={(e) =>
                                      updateSpecificationField(
                                        specIndex,
                                        "value",
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Specification ${
                                      specIndex + 1
                                    } value`}
                                    disabled={isSubmittingForm}
                                  />
                                </div>
                                <div className="col-md-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() =>
                                      removeSpecificationAtIndex(specIndex)
                                    }
                                    disabled={
                                      productSpecifications.length <= 1 ||
                                      isSubmittingForm
                                    }
                                    title="Remove specification"
                                    style={{ borderRadius: "20px" }}>
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </div>
                              </div>
                              {formValidationErrors[`spec-${specIndex}`] && (
                                <div className="col-12">
                                  <div className="text-danger">
                                    {formValidationErrors[`spec-${specIndex}`]
                                      .split("\n")
                                      .map((error, errorIndex) => (
                                        <small
                                          key={errorIndex}
                                          className="d-block">
                                          {error}
                                        </small>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>

                      <button
                        title="Add More Specification"
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-3"
                        onClick={addNewSpecification}
                        disabled={isSubmittingForm}
                        style={{ borderRadius: "20px" }}>
                        <i className="ti ti-plus me-1"></i>
                        Add More
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer border-top">
                <button
                  title="Cancel Add Product Form"
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmittingForm}
                  onClick={resetProductForm}>
                  Cancel
                </button>
                <button
                  title="Submit the add product form"
                  type="submit"
                  className="btn btn-primary"
                  disabled={isAnyProcessing}>
                  {isSubmittingForm ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"></span>
                      Adding Product...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-plus me-2"></i>
                      Add Product
                    </>
                  )}
                </button>
                <button
                  id="add-product-dismiss-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductModal;
